import { useQueryClient } from '@tanstack/react-query'
import { ConsignmentItemFormValues } from 'features/declaration/consignment-item/models/schema'
import { QueryKeys } from '../../../key-registry'
import { ErrorResult } from '../../../../shared/models/types'
import useSaveConsignmentItem from './useSaveConsignmentItem'
import { ConsignmentItemDetails } from '../models/types'
import useSaveConsignmentItemDocuments from '../../consignment-item-document/components/useSaveConsignmentItemDocuments'
import useDeleteConsignmentItemDocuments from '../../consignment-item-document/components/useDeleteConsignmentItemDocuments'
import useSaveAdditionalReferenceFormValues from '../../additional-reference/components/useSaveAdditionalReferences'
import { AdditionalReference } from '../../additional-reference/models/types'
import useDeleteAdditionalReferences from '../../additional-reference/components/useDeleteAdditionalReferences'
import { Document } from '../../document/models/types'
import computeConsignmentDocumentDeletes from '../../consignment-document/models/computeConsignmentDocumentDeletes'

export interface ConsignmentItemFormValuesSaveResult {
  consignmentItem: ConsignmentItemDetails
  consignmentItemDocuments: Document[]
  additionalReferences: AdditionalReference[]
}

interface SaveConsignmentItemFormResult {
  data: ConsignmentItemFormValuesSaveResult
  hasPartialDocumentWrite: boolean
  errors: ErrorResult[]
}

interface UseSaveConsignmentItemFormProps {
  id: number | null
  consignmentId: number
  transitOperationId: number
  houseConsignmentId: number
  existingDocuments: Document[]
}

function useSaveConsignmentItemForm({
  id,
  consignmentId,
  transitOperationId,
  houseConsignmentId,
  existingDocuments,
}: UseSaveConsignmentItemFormProps) {
  const { saveConsignmentItem } = useSaveConsignmentItem()
  const { deleteConsignmentItemDocuments } = useDeleteConsignmentItemDocuments()
  const { saveConsignmentItemDocuments } = useSaveConsignmentItemDocuments(transitOperationId)
  const { saveAdditionalReferences } = useSaveAdditionalReferenceFormValues()
  const { deleteAdditionalReferences } = useDeleteAdditionalReferences()

  const queryClient = useQueryClient()

  const writeConsignmentItem = async (
    formValues: ConsignmentItemFormValues,
    isDraft: boolean,
  ): Promise<SaveConsignmentItemFormResult> => {
    const savedConsignmentItem = await saveConsignmentItem({
      id,
      isDraft,
      houseConsignmentId,
      formValues,
    })

    if (!savedConsignmentItem?.id) throw new Error('Consignment item save failed')

    const {
      data: savedDocuments,
      documentErrors: saveDocumentWriteErrors,
      errors: saveDocumentErrors,
    } = await saveConsignmentItemDocuments(savedConsignmentItem.id, formValues)

    const documentsToDelete = computeConsignmentDocumentDeletes(existingDocuments, formValues)
    let deleteConsignmentItemDocumentErrors: ErrorResult[] = []
    if (!saveDocumentWriteErrors.length) {
      const archiveResult = await deleteConsignmentItemDocuments({ documentsToDelete })
      deleteConsignmentItemDocumentErrors = archiveResult.errors
    }

    const { errors: deleteAdditionalReferenceErrors } = await deleteAdditionalReferences(formValues.additionalReference
      .filter((o) => o.id != null && o.deleted)
      .map((o) => o.id as number))

    const { data: savedAdditionalReferences, errors: saveAdditionalReferenceErrors } = await saveAdditionalReferences(
      transitOperationId,
      savedConsignmentItem.id,
      'consignmentItem',
      formValues.additionalReference,
      isDraft,
    )

    const allErrors = [
      ...saveDocumentErrors,
      ...deleteConsignmentItemDocumentErrors,
      ...saveAdditionalReferenceErrors,
      ...deleteAdditionalReferenceErrors,
    ]

    const documents: Document[] = savedDocuments ?? []

    return {
      data: {
        consignmentItem: savedConsignmentItem,
        consignmentItemDocuments: documents,
        additionalReferences: savedAdditionalReferences ?? [],
      },
      hasPartialDocumentWrite: saveDocumentWriteErrors.length > 0,
      errors: allErrors,
    }
  }

  const updateCaches = async (args: SaveConsignmentItemFormResult) => {
    const {
      consignmentItem: savedConsignmentItem,
      consignmentItemDocuments: savedDocuments,
      additionalReferences: savedAdditionalReferences,
    } = args.data
    if (!savedConsignmentItem?.id) throw new Error('Missing consignment item ID')

    queryClient.setQueryData(QueryKeys.consignmentItem.details(savedConsignmentItem.id), savedConsignmentItem)
    if (!args.hasPartialDocumentWrite) {
      queryClient.setQueryData(QueryKeys.consignmentItemDocument.byConsignmentItemId(savedConsignmentItem.id), savedDocuments)
    }
    queryClient.setQueryData(QueryKeys.additionalReference.allByParams({
      parentId: savedConsignmentItem.id,
      parentType: 'consignmentItem',
    }), savedAdditionalReferences)
    await queryClient.invalidateQueries(QueryKeys.consignmentItem.all({ consignmentId }))
    await queryClient.invalidateQueries(QueryKeys.consignment.summary(consignmentId))
    await queryClient.invalidateQueries(QueryKeys.transitOperation.validation({ transitOperationId }))
  }

  const saveConsignmentItemFormValues = async ({
    values,
    isDraft,
  } : {
    values: ConsignmentItemFormValues
    isDraft: boolean
  }) => {
    const result = await writeConsignmentItem(values, isDraft)
    await updateCaches(result)
    return result
  }

  return {
    saveConsignmentItemFormValues,
  }
}

export default useSaveConsignmentItemForm
